import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ConfirmModal, type ConfirmModalProps } from '@/components/shared/ConfirmModal';

type Options = Pick<ConfirmModalProps, 'title' | 'message' | 'confirmLabel' | 'variant'>;

/** One pending decision per caller; unmount cancels without performing an action. */
export function useConfirmation() {
  const [options, setOptions] = useState<Options | null>(null);
  const pending = useRef<((accepted: boolean) => void) | null>(null);
  useEffect(() => () => { pending.current?.(false); pending.current = null; }, []);
  const ask = useCallback((next: Options): Promise<boolean> => {
    if (pending.current) return Promise.resolve(false);
    return new Promise((resolve) => { pending.current = resolve; setOptions(next); });
  }, []);
  const finish = (accepted: boolean) => {
    const resolve = pending.current;
    pending.current = null;
    setOptions(null);
    resolve?.(accepted);
  };
  const dialog = options ? createPortal(<ConfirmModal {...options}
    onConfirm={() => finish(true)} onCancel={() => finish(false)} />, document.body) : null;
  return { ask, dialog };
}
